import VistaprintAuth from "@vp/auth";
import { IdMeVerification } from "@vp/id.me-verification";
import { Button, Icon, Link } from "@vp/swan";
import React from "react";
import { useBannerContext } from "../../context/BannerContext";
import { CTAButtonProps } from "../../types/banner";
import { getLinkIdentifier, getTextProps } from "../../utils";

const auth = new VistaprintAuth.WebAuth();

const CTAButton: React.FunctionComponent<CTAButtonProps> = ({
  skin = "primary",
  label = "",
  href = {},
  type,
  trackingHandler,
  ctaPosition,
  openInNewTab = false,
  isDoubleBanner,
  firstBannerCTAButtons = 0,
  gmpvUploadButton = {},
  gmpvFlowComponent = <></>,
  locale,
  handleOnSuccessfulApproval,
  shopperIsInMilitaryGroup,
  loading,
}) => {
  if (gmpvUploadButton?.mpvId) {
    return gmpvFlowComponent;
  }

  const { actionId, trackingId, href: path = "", linkType } = href;
  const linkIdentifier = getLinkIdentifier(linkType, actionId, trackingId);
  const trackingPosition = ctaPosition ?? 0;

  const { isSignedIn, customerId } = useBannerContext();

  const handleTracking = (event: React.MouseEvent) => {
    if (trackingHandler) {
      trackingHandler(event, {
        destinationUrl: path,
        linkIdentifier: linkIdentifier as string,
        ctaPosition: isDoubleBanner
          ? `2:${firstBannerCTAButtons + trackingPosition}`
          : `${trackingPosition}`,
        openInNewTab,
      });
    }
  };

  const handleSignIn = () => {
    auth?.signIn();
  };

  const processedLabel = getTextProps(label);

  switch (type) {
    case "link":
      return processedLabel ? (
        <Link href={path} onClick={handleTracking} target={openInNewTab ? "_blank" : "_self"}>
          {processedLabel}
        </Link>
      ) : (
        <></>
      );
    case "ctaLink":
      return processedLabel ? (
        <Link
          skin='cta'
          href={path}
          onClick={handleTracking}
          target={openInNewTab ? "_blank" : "_self"}
        >
          {processedLabel}
        </Link>
      ) : (
        <></>
      );
    case "startChatButton":
      return processedLabel ? (
        <Button skin={skin} iconPosition='left' id='start-chat-button' onClick={handleTracking}>
          {processedLabel}
          <Icon alt='' iconType='liveChat' />
        </Button>
      ) : (
        <></>
      );
    case "verifyIdMeButton":
      if (loading || !auth || !processedLabel) return null;

      if (!isSignedIn) {
        return (
          <Button skin={skin} id='sign-in-button' onClick={handleSignIn}>
            {processedLabel}
          </Button>
        );
      }

      if (!shopperIsInMilitaryGroup && customerId && locale) {
        return (
          <IdMeVerification
            locale={locale}
            customerId={customerId}
            onSuccessfulVerification={handleOnSuccessfulApproval}
          />
        );
      }

      return null;

    default:
      return processedLabel ? (
        <Button
          skin={skin}
          render={(p) => (
            <Link href={path} className={p.className} onClick={handleTracking}>
              {processedLabel}
            </Link>
          )}
        />
      ) : (
        <></>
      );
  }
};

export { CTAButton };
